import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Car, Mountain } from 'lucide-react';
import { Hero } from '../components/Hero';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Home = () => {
  const { t, language } = useLanguage();
  const [rooms, setRooms] = useState([]);
  const [upsells, setUpsells] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [roomsRes, upsellsRes] = await Promise.all([
          axios.get(`${API}/rooms`),
          axios.get(`${API}/upsells`),
        ]);
        setRooms(roomsRes.data.slice(0, 3));
        setUpsells(upsellsRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const experienceCategories = [
    { icon: Sparkles, key: 'spa', label: t('experiences.spa') },
    { icon: Car, key: 'shuttle', label: t('experiences.shuttle') },
    { icon: Mountain, key: 'excursion', label: t('experiences.excursion') },
  ];

  return (
    <div data-testid="home-page">
      <Hero />

      {/* About Section */}
      <section className="py-24 lg:py-32 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-sans font-medium mb-4">
                Welcome
              </p>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#0F1F3C] leading-tight mb-6">
                A Mediterranean Haven of Timeless Luxury
              </h2>
              <p className="text-[#64748B] font-sans leading-relaxed mb-8">
                Nestled along the pristine shores of the French Riviera, Le Château d'Azur 
                offers an unparalleled experience where classic elegance meets modern sophistication. 
                Our historic estate, dating back to the Belle Époque, has been meticulously restored 
                to provide discerning travelers with the ultimate sanctuary.
              </p>
              <Link
                to="/gallery"
                className="inline-flex items-center space-x-2 text-[#0F1F3C] text-xs uppercase tracking-widest font-sans font-medium hover:text-[#D4AF37] transition-colors group"
              >
                <span>Discover Our Story</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1562371589-d02ea4e9754e?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Hotel exterior"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-8 -left-8 bg-[#0F1F3C] text-white p-8 hidden lg:block">
                <p className="font-serif text-4xl">25+</p>
                <p className="text-xs uppercase tracking-wider text-white/60 font-sans mt-2">
                  Years of Excellence
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Rooms Preview */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-sans font-medium mb-4">
              Accommodations
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#0F1F3C]">
              {t('rooms.title')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rooms.map((room, index) => (
              <motion.div
                key={room.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <Link to={`/rooms/${room.id}`}>
                  <div className="relative overflow-hidden mb-4">
                    <img
                      src={room.images[0]}
                      alt={room.name[language] || room.name.en}
                      className="w-full h-[300px] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="font-serif text-xl text-[#0F1F3C] group-hover:text-[#D4AF37] transition-colors mb-2">
                    {room.name[language] || room.name.en}
                  </h3>
                  <p className="text-[#0F1F3C]">
                    <span className="text-xs uppercase tracking-wider text-[#64748B] font-sans">{t('rooms.from')}</span>
                    <span className="font-serif text-xl ml-2">€{room.price_per_night}</span>
                    <span className="text-sm text-[#64748B] font-sans">/{t('booking.night')}</span>
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/rooms"
              className="inline-block border border-[#0F1F3C] text-[#0F1F3C] hover:bg-[#0F1F3C] hover:text-white px-10 py-4 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300"
              data-testid="view-all-rooms-btn"
            >
              View All Rooms
            </Link>
          </div>
        </div>
      </section>

      {/* Experiences Preview */}
      <section className="py-24 lg:py-32 bg-[#0F1F3C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-sans font-medium mb-4">
              Experiences
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl text-white">
              {t('experiences.title')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {experienceCategories.map((category, index) => (
              <motion.div
                key={category.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/experiences?category=${category.key}`}
                  className="block p-8 border border-white/10 hover:border-[#D4AF37]/50 transition-colors group text-center"
                >
                  <category.icon className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
                  <h3 className="font-serif text-2xl text-white group-hover:text-[#D4AF37] transition-colors">
                    {category.label}
                  </h3>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/experiences"
              className="inline-block border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white px-10 py-4 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300"
              data-testid="view-experiences-btn"
            >
              Explore All Experiences
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1736618625396-571234ade4d7?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Pool"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0F1F3C]/70" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl lg:text-5xl text-white mb-6">
              Begin Your Journey
            </h2>
            <p className="text-white/70 font-sans mb-10 text-lg">
              Let us curate an unforgettable experience tailored to your desires.
            </p>
            <Link
              to="/rooms"
              className="inline-block bg-[#D4AF37] text-white hover:bg-[#B5952F] px-12 py-5 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300"
              data-testid="cta-book-btn"
            >
              {t('nav.bookNow')}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
